/**
 * Ce que les donnees ne disent pas encore — et comment y remedier.
 *
 * Le moteur repond « donnees insuffisantes » sur la grande majorite des couples
 * pendant les premiers mois. C'est honnete, mais inexploitable : l'utilisateur
 * ne sait ni pourquoi, ni ce qui manque, ni sur quoi porter son effort.
 *
 * Ce module transforme ces silences en indications concretes :
 *  - quels aliments n'ont jamais ete observes, ou trop peu ;
 *  - combien d'observations manquent exactement pour qu'un test soit possible ;
 *  - quels aliments sont indissociables et gagneraient a etre separes.
 *
 * ## Ce qu'il ne fait pas
 *
 * Il ne prescrit jamais de manger quoi que ce soit. Un aliment jamais consomme
 * l'est souvent deliberement, et rien ici ne sait pourquoi. Les suggestions
 * portent sur ce qui est **deja** au menu : consommer un aliment sans son
 * inseparable, ou simplement continuer a noter.
 */

import type { Association, OptionsAnalyse, ResultatAnalyse } from './types'
import type { Id, Ingredient, Meal, SymptomType } from '../domain/types'
import { SYMPTOM_LABELS } from '../domain/types'

// ---------------------------------------------------------------------------
// Statut d'un ingredient vis-a-vis de l'analyse
// ---------------------------------------------------------------------------

export type StatutAnalyse =
  /** Jamais vu dans un repas : l'analyse n'a rien a en dire. */
  | 'jamais'
  /** Vu, mais trop peu pour qu'un test soit lance. */
  | 'insuffisant'
  /** Teste, sans association retenue. */
  | 'analyse'
  /** Association faible, a confirmer. */
  | 'surveiller'
  /** Association retenue. */
  | 'piste'

export const STATUT_LABELS: Record<StatutAnalyse, string> = {
  jamais: 'Jamais consommé',
  insuffisant: 'Trop peu de données',
  analyse: 'Analysé',
  surveiller: 'À surveiller',
  piste: 'Piste sérieuse',
}

export interface StatutIngredient {
  statut: StatutAnalyse
  /** Nombre de repas contenant cet ingredient. */
  repas: number
  /** Repas manquants pour qu'un test devienne possible ; 0 si deja teste. */
  manquants: number
}

/** Compte les repas contenant chaque ingredient. */
export function occurrencesParIngredient(repas: readonly Meal[]): Map<Id, number> {
  const out = new Map<Id, number>()
  for (const m of repas) {
    // Un ingredient present deux fois dans un repas ne compte qu'une fois.
    const vus = new Set(m.ingredientsResolus.map((r) => r.ingredientId))
    for (const id of vus) out.set(id, (out.get(id) ?? 0) + 1)
  }
  return out
}

/**
 * Estime le nombre de repas exposes manquants pour qu'un test soit lance.
 *
 * Franchir ce seuil ne garantit pas de conclure : cela rend seulement le test
 * possible. L'interface doit le dire, sous peine de promettre une reponse au
 * bout de trois repas.
 */
function repasManquants(a: Association | undefined, o: OptionsAnalyse, repas: number): number {
  if (!a) return Math.max(0, o.minRepasExposes - repas)
  if (a.repasExposes < o.minRepasExposes) return o.minRepasExposes - a.repasExposes
  if (a.contingence.a < o.minEvenements) {
    // Il ne manque plus des expositions mais des declenchements : on extrapole
    // avec le taux observe, minore pour ne pas annoncer un chiffre absurde
    // quand aucun declenchement n'a encore ete vu.
    const taux = Math.max(a.risqueExpose, a.risqueNonExpose, 0.05)
    return Math.ceil((o.minEvenements - a.contingence.a) / taux)
  }
  return 0
}

export function statutsIngredients(
  repas: readonly Meal[],
  ingredients: readonly Ingredient[],
  resultat: ResultatAnalyse,
): Map<Id, StatutIngredient> {
  const occurrences = occurrencesParIngredient(repas)
  const o = resultat.options

  // Meilleure association connue pour chaque ingredient, tous symptomes confondus.
  const meilleures = new Map<Id, Association>()
  for (const a of resultat.associations) {
    if (a.niveau !== 'ingredient') continue
    const id = a.cle.slice('ingredient:'.length)
    const actuelle = meilleures.get(id)
    if (!actuelle || rang(a) < rang(actuelle)) meilleures.set(id, a)
  }

  const out = new Map<Id, StatutIngredient>()
  for (const ing of ingredients) {
    const n = occurrences.get(ing.id) ?? 0
    const a = meilleures.get(ing.id)

    let statut: StatutAnalyse
    if (n === 0) statut = 'jamais'
    else if (!a || a.verdict === 'donnees-insuffisantes') statut = 'insuffisant'
    else if (a.verdict === 'piste-serieuse') statut = 'piste'
    else if (a.verdict === 'a-surveiller') statut = 'surveiller'
    else statut = 'analyse'

    out.set(ing.id, {
      statut,
      repas: n,
      manquants: statut === 'jamais' || statut === 'insuffisant' ? repasManquants(a, o, n) : 0,
    })
  }
  return out
}

function rang(a: Association): number {
  return { 'piste-serieuse': 0, 'a-surveiller': 1, 'probablement-non-lie': 2, 'donnees-insuffisantes': 3 }[
    a.verdict
  ]
}

// ---------------------------------------------------------------------------
// Suggestions
// ---------------------------------------------------------------------------

export type TypeSuggestion = 'a-isoler' | 'a-confirmer' | 'trop-peu' | 'jamais-teste'

export interface Suggestion {
  id: string
  type: TypeSuggestion
  titre: string
  explication: string
  action: string
  /** Tri decroissant : ce qui apporte le plus d'information d'abord. */
  priorite: number
  /** Cle d'exposition concernee, pour renvoyer vers le detail. */
  cle?: string
  symptome?: SymptomType
}

export interface EntreeSuggestions {
  repas: readonly Meal[]
  ingredients: readonly Ingredient[]
  resultat: ResultatAnalyse
  /** Nombre maximal de suggestions rendues. */
  limite?: number
}

export function construireSuggestions(e: EntreeSuggestions): Suggestion[] {
  const { repas, ingredients, resultat } = e
  const o = resultat.options
  const nomDe = new Map(ingredients.map((i) => [i.id, i.nom]))
  const occurrences = occurrencesParIngredient(repas)
  const suggestions: Suggestion[] = []

  // --- Aliments indissociables ---------------------------------------------
  // C'est le cas ou une seule observation bien choisie leve une ambiguite que
  // des mois de journal ne leveront jamais autrement.
  //
  // Les ensembles sont indexes par **cle d'exposition** et non par identifiant
  // d'ingredient : un bloc indissociable peut contenir des recettes autant que
  // des ingredients (cf. `detecterBlocsIndissociables`), et une cle
  // `recette:...` n'a aucune chance d'etre retrouvee parmi les ingredients.
  const ensembles = new Map<string, Set<number>>()
  const noter = (cle: string, idx: number) => {
    const s = ensembles.get(cle)
    if (s) s.add(idx)
    else ensembles.set(cle, new Set([idx]))
  }
  repas.forEach((m, idx) => {
    for (const r of new Set(m.ingredientsResolus.map((x) => x.ingredientId))) {
      noter(`ingredient:${r}`, idx)
    }
    for (const s of m.sources) {
      if (s.kind === 'recipe') noter(`recette:${s.recipeId}`, idx)
    }
  })

  const impliqueDansPiste = new Set(
    resultat.associations
      .filter((a) => a.verdict === 'piste-serieuse' || a.verdict === 'a-surveiller')
      .map((a) => a.cle),
  )

  for (const bloc of resultat.blocsIndissociables) {
    // Les blocs de charges glissantes sont ecartes : les signaler comme
    // indissociables a du sens — cela evite de lire un artefact unique comme
    // deux resultats concordants — mais en faire une suggestion n'en a aucun.
    // « Mangez du fructose sans polyols » ne se cuisine pas : une charge est
    // une grandeur derivee, pas quelque chose qui se sert a part. Une
    // suggestion doit rester une action realisable au prochain repas.
    if (bloc.cles.every((c) => c.startsWith('charge:'))) continue

    // Les libelles viennent du moteur, qui a resolu chaque cle au moment ou il
    // l'a construite. Les recalculer ici depuis les cles est ce qui affichait
    // des identifiants bruts des qu'une recette entrait dans un bloc.
    const noms = bloc.libelles
    const prioritaire = bloc.cles.some((c) => impliqueDansPiste.has(c))

    // Combien de fois chacun a-t-il ete consomme sans les autres ?
    const seuls = bloc.cles.map((cle, k) => {
      const mien = ensembles.get(cle) ?? new Set<number>()
      const autres = bloc.cles
        .filter((_, j) => j !== k)
        .map((x) => ensembles.get(x) ?? new Set<number>())
      let n = 0
      for (const idx of mien) if (!autres.some((s) => s.has(idx))) n++
      return n
    })
    const leMoinsSeul = seuls.indexOf(Math.min(...seuls))

    suggestions.push({
      id: `isoler-${bloc.cles.join('+')}`,
      type: 'a-isoler',
      titre: `${noms.join(' et ')} : impossible de les départager`,
      explication: prioritaire
        ? `Ces aliments apparaissent presque toujours ensemble et l'un d'eux ressort déjà dans les analyses — mais rien ne permet de savoir lequel est en cause.`
        : `Ces aliments apparaissent presque toujours ensemble dans vos repas : leurs statistiques sont identiques et le resteront tant qu'ils ne seront pas séparés.`,
      action:
        seuls[leMoinsSeul] === 0
          ? `Consommer ${noms[leMoinsSeul]} sans ${noms.filter((_, k) => k !== leMoinsSeul).join(' ni ')} — cela n'est encore jamais arrivé dans votre journal.`
          : `Consommer ${noms[leMoinsSeul]} seul plus souvent : cela n'est arrivé que ${seuls[leMoinsSeul]} fois, trop peu pour trancher.`,
      priorite: prioritaire ? 100 : 70,
      cle: bloc.cles[0] ?? '',
    })
  }

  // --- Pistes faibles a confirmer ------------------------------------------
  for (const a of resultat.associations) {
    if (a.verdict !== 'a-surveiller') continue
    const manque = Math.max(4, Math.ceil(a.repasExposes * 0.5))
    suggestions.push({
      id: `confirmer-${a.cle}-${a.symptome}`,
      type: 'a-confirmer',
      titre: `${a.libelle} — ${SYMPTOM_LABELS[a.symptome].toLowerCase()}`,
      explication: `Le signal existe (risque ×${a.risqueRelatif.toFixed(1)} sur ${a.repasExposes} repas) mais ne franchit pas le seuil de fiabilité. Il peut aussi bien se confirmer que disparaître.`,
      action: `Continuer à noter : environ ${manque} repas exposés de plus devraient permettre de trancher.`,
      priorite: 80,
      cle: a.cle,
      symptome: a.symptome,
    })
  }

  // --- Aliments consommes mais pas encore analysables ------------------------
  const parIngredient = new Map<Id, Association>()
  for (const a of resultat.associations) {
    if (a.niveau !== 'ingredient') continue
    const id = a.cle.slice('ingredient:'.length)
    const c = parIngredient.get(id)
    if (!c || a.repasExposes > c.repasExposes) parIngredient.set(id, a)
  }

  const presqueLa: { id: Id; nom: string; n: number; manque: number }[] = []
  for (const [id, n] of occurrences) {
    // Un repas peut referencer un ingredient absent du referentiel — un import
    // de sauvegarde partiel, par exemple. Le moteur les ignore deja
    // (`construireExpositions`) : promettre ici qu'ils deviendront analysables
    // serait faux, et les afficher revenait a montrer leur identifiant brut.
    const nom = nomDe.get(id)
    if (nom === undefined) continue
    const a = parIngredient.get(id)
    if (a && a.verdict !== 'donnees-insuffisantes') continue
    const manque = repasManquants(a, o, n)
    if (manque > 0) presqueLa.push({ id, nom, n, manque })
  }
  presqueLa.sort((x, y) => x.manque - y.manque || y.n - x.n)

  for (const p of presqueLa.slice(0, 6)) {
    suggestions.push({
      id: `trop-peu-${p.id}`,
      type: 'trop-peu',
      titre: p.nom,
      explication: `Présent dans ${p.n} repas — pas encore assez pour qu'un test statistique soit possible.`,
      action: `Environ ${p.manque} repas de plus en contenant rendraient l'analyse possible.`,
      priorite: 40 - Math.min(20, p.manque),
      cle: `ingredient:${p.id}`,
    })
  }

  suggestions.sort((a, b) => b.priorite - a.priorite || a.titre.localeCompare(b.titre, 'fr'))
  return suggestions.slice(0, e.limite ?? 12)
}

// ---------------------------------------------------------------------------
// Couverture globale du referentiel
// ---------------------------------------------------------------------------

export interface Couverture {
  total: number
  jamais: number
  insuffisant: number
  analyse: number
  surveiller: number
  piste: number
}

export function couverture(statuts: Map<Id, StatutIngredient>): Couverture {
  const c: Couverture = { total: 0, jamais: 0, insuffisant: 0, analyse: 0, surveiller: 0, piste: 0 }
  for (const s of statuts.values()) {
    c.total++
    c[s.statut]++
  }
  return c
}
